import { Display } from "rot-js"
import { ItemType, PlayerAction } from "../defines/enums"
import { Offset } from "../defines/types"
import { EngineDefaults } from "../defines/values"
import { BLACK_TILE, BORDER_TILE, TapestryTile } from "../map/tile"
import { toDisplayString } from "../utils/color"
import { getMaxBindersAvailable } from "../utils/misc"
import { Popup } from "./popup"

export class Archive extends Popup {
  private _tiles: TapestryTile[][] = []

  constructor(offsets: Offset, name: string) {
    super(
      EngineDefaults.TROPHY_CASE_WIDTH,
      EngineDefaults.TROPHY_CASE_HEIGHT,
      offsets,
      name
    )
    this.initTiles()
  }

  prepareToRender(): void {
    return
  }

  postRender(): void {}

  render(display: Display): void {
    for (let y = 0; y < this._tiles.length; ++y) {
      const row = this._tiles[y]
      for (let x = 0; x < row.length; ++x) {
        const tile = row[x]
        display.draw(
          x + this.offsets.xOffset,
          y + this.offsets.yOffset,
          tile.symbol,
          toDisplayString(tile.fg),
          toDisplayString(tile.bg)
        )
      }
    }

    const colors: string = "%c{#fff}%b{#000}"
    const blankColors = "%c{#000}%b{#000}"
    const title: string = "Archive"
    let printRow: number = 1
    let xStart: number = this.width / 2 - title.length / 2

    display.drawText(xStart, printRow, colors + title)

    printRow += 1
    xStart = this.offsets.xOffset + 1
    display.drawText(
      xStart,
      printRow,
      colors + "-".padEnd(this.width - 2, "-") // 2 accounts for walls
    )

    // 6 is the rows consumed by walls + the header
    // 5 is the number of sections
    const sectionHeight = (this.height - 6) / 5

    const headingColors = "%c{#0ff}%b{#000}"

    let titleXStart = xStart + 1
    let statsXStart = titleXStart + 2

    printRow += 1
    // Tapestry
    display.drawText(
      titleXStart,
      printRow,
      headingColors + "Completing the tapestry"
    )
    printRow += 1

    let tapestryOutput = ""

    const tapestryStats = window.engine.getTapestryStats()
    if (tapestryStats !== undefined) {
      const percent = tapestryStats.percentCompleted.toFixed(1)
      tapestryOutput = "You have restored " + percent + "% of the tapestry."
      display.drawText(statsXStart, printRow, colors + tapestryOutput)

      printRow += 1
      const tiles = tapestryStats.tilesCompleted
      const totalTiles = tapestryStats.totalColorTiles
      tapestryOutput =
        "You have bound pigment to " + tiles + " of " + totalTiles + " tiles."
      display.drawText(statsXStart, printRow, colors + tapestryOutput)
    } else {
      printRow += 1
    }

    printRow += 1

    // Blank line
    tapestryOutput = ".".repeat(this.width - 2)
    display.drawText(2, printRow, blankColors + tapestryOutput)

    // Pigments/binders
    printRow += 1
    display.drawText(
      titleXStart,
      printRow,
      headingColors + "Gathering pigments and binders"
    )

    const player = window.engine.getPlayer()

    const pigments = player.getPigmentCollected()
    const binders = player.bindersCollected
    const bindersAvailable = getMaxBindersAvailable()
    printRow += 1
    let lead = "Binders gathered ->  "
    tapestryOutput =
      "Jan (" +
      binders.jan +
      "/" +
      bindersAvailable.jan +
      "), Feb (" +
      binders.feb +
      "/" +
      bindersAvailable.feb +
      "), Mar (" +
      binders.mar +
      "/" +
      bindersAvailable.mar +
      "), Apr (" +
      binders.apr +
      "/" +
      bindersAvailable.apr +
      ")"
    display.drawText(statsXStart, printRow, colors + lead + tapestryOutput)

    printRow += 1
    tapestryOutput = ".".repeat(statsXStart + lead.length)
    display.drawText(statsXStart, printRow, blankColors + tapestryOutput)

    tapestryOutput =
      "May (" +
      binders.may +
      "/" +
      bindersAvailable.may +
      "), Jun (" +
      binders.jun +
      "/" +
      bindersAvailable.jun +
      "), Jul (" +
      binders.jul +
      "/" +
      bindersAvailable.jul +
      "), Aug (" +
      binders.aug +
      "/" +
      bindersAvailable.aug +
      ")"
    display.drawText(
      statsXStart + lead.length,
      printRow,
      colors + tapestryOutput
    )

    printRow += 1
    tapestryOutput =
      "Sep (" +
      binders.sep +
      "/" +
      bindersAvailable.sep +
      "), Oct (" +
      binders.oct +
      "/" +
      bindersAvailable.oct +
      "), Nov (" +
      binders.nov +
      "/" +
      bindersAvailable.nov +
      "), Dec (" +
      binders.dec +
      "/" +
      bindersAvailable.dec +
      ")"
    display.drawText(
      statsXStart + lead.length,
      printRow,
      colors + tapestryOutput
    )

    printRow += 2
    lead = "Pigments gathered -> "
    tapestryOutput =
      "Red (" +
      pigments.red.toString().padStart(3) +
      "), Gre (" +
      pigments.green.toString().padStart(3) +
      "), Blu (" +
      pigments.blue.toString().padStart(3) +
      ")"
    display.drawText(statsXStart, printRow, colors + lead + tapestryOutput)

    printRow += 1
    tapestryOutput = ".".repeat(statsXStart + lead.length)
    display.drawText(statsXStart, printRow, blankColors + tapestryOutput)

    tapestryOutput =
      "Yel (" +
      pigments.yellow.toString().padStart(3) +
      "), Mag (" +
      pigments.magenta.toString().padStart(3) +
      "), Cyn (" +
      pigments.cyan.toString().padStart(3) +
      ")"
    display.drawText(
      statsXStart + lead.length,
      printRow,
      colors + tapestryOutput
    )

    // Blank line
    printRow += 1
    tapestryOutput = ".".repeat(this.width - 2)
    display.drawText(2, printRow, blankColors + tapestryOutput)

    // Map visits + deaths
    printRow += 1
    display.drawText(titleXStart, printRow, headingColors + "Exploring the map")

    printRow += 1
    const visits = player.visits
    lead = "You have explored -> "
    tapestryOutput =
      "Jan (" +
      visits.january.toString().padStart(3) +
      "), Feb (" +
      visits.february.toString().padStart(3) +
      "), Mar (" +
      visits.march.toString().padStart(3) +
      "), Apr (" +
      visits.april.toString().padStart(3) +
      ")"
    display.drawText(statsXStart, printRow, colors + lead + tapestryOutput)

    printRow += 1
    tapestryOutput =
      "May (" +
      visits.may.toString().padStart(3) +
      "), Jun (" +
      visits.june.toString().padStart(3) +
      "), Jul (" +
      visits.july.toString().padStart(3) +
      "), Aug (" +
      visits.august.toString().padStart(3) +
      ")"
    display.drawText(
      statsXStart + lead.length,
      printRow,
      colors + tapestryOutput
    )

    printRow += 1
    tapestryOutput =
      "Sep (" +
      visits.september.toString().padStart(3) +
      "), Oct (" +
      visits.october.toString().padStart(3) +
      "), Nov (" +
      visits.november.toString().padStart(3) +
      "), Dec (" +
      visits.december.toString().padStart(3) +
      ")"
    display.drawText(
      statsXStart + lead.length,
      printRow,
      colors + tapestryOutput
    )

    // Equipment
    printRow += 2
    display.drawText(titleXStart, printRow, headingColors + "Finding equipment")

    const wearables = player.getWearablesCount()
    printRow += 1
    tapestryOutput = "You have found " + wearables + " wearable item"
    if (wearables != 1) {
      tapestryOutput += "s"
    }
    tapestryOutput += "."
    display.drawText(statsXStart, printRow, colors + tapestryOutput)

    const weapons = player.getWeaponsCount()
    const gizmos = player.getGizmosCount()
    printRow += 1
    tapestryOutput = "You have found " + weapons + " weapon"
    if (weapons != 1) {
      tapestryOutput += "s"
    }
    tapestryOutput += " and " + gizmos + " gizmo"
    if (gizmos != 1) {
      tapestryOutput += "s"
    }
    tapestryOutput += "."
    display.drawText(statsXStart, printRow, colors + tapestryOutput)

    // Blank line
    printRow += 1
    tapestryOutput = ".".repeat(this.width - 2)
    display.drawText(2, printRow, blankColors + tapestryOutput)

    // Hints
    printRow += 1
    display.drawText(titleXStart, printRow, headingColors + "Discovering hints")

    const infoHints = player.getHints(ItemType.HINT_INFO)
    const warningHints = player.getHints(ItemType.HINT_WARNING)
    const hints = infoHints.length + warningHints.length
    printRow += 1
    tapestryOutput = "You have discovered " + hints + " hint"
    if (hints != 1) {
      tapestryOutput += "s"
    }
    tapestryOutput += "."
    display.drawText(statsXStart, printRow, colors + tapestryOutput)

    // Deaths
    printRow += 2
    display.drawText(titleXStart, printRow, headingColors + "Facing challenges")

    const deaths = player.deaths
    printRow += 1
    tapestryOutput = "You have been drained of your essence " + deaths + " time"
    if (deaths != 1) {
      tapestryOutput += "s"
    }
    tapestryOutput += "."
    display.drawText(statsXStart, printRow, colors + tapestryOutput)
  }

  update(action: PlayerAction): void {
    if (action == PlayerAction.MOVE_DOWN) {
    } else if (action == PlayerAction.MOVE_UP) {
    }
  }

  protected initTiles() {
    this._tiles = new Array(this.height)
    for (let y = 0; y < this.height; y++) {
      const row = new Array(this.width)
      for (let x = 0; x < this.width; x++) {
        const isWall =
          x === 0 || x === this.width - 1 || y === 0 || y === this.height - 1
        row[x] = isWall ? { ...BORDER_TILE } : { ...BLACK_TILE }
      }
      this._tiles[y] = row
    }
  }
}
